// Sidebar's home-scope content (Workspaces section + global Direct Messages
// section) — extracted from Sidebar.tsx during the P5 pass-3 split. Pure
// render: every input is a prop, no state, no effects.

import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { NavRow } from "@/components/ui/nav-row";
import { SectionLabel } from "@/components/ui/section-label";
import { cn } from "@/lib/utils";
import { WorkspaceIcon } from "@/components/ui/workspace-icon";
import { DMList } from "@/components/dm/DMList";
import { HomeSectionPill } from "./home-section-pill";
import { SidebarSectionToggle } from "./sidebar-section-toggle";
import { getRoleLabel } from "./role-helpers";

type WorkspaceLike = {
  id: string;
  name: string;
  slug: string;
  logo_url?: string | null;
};

type HomeFilter = "all" | "recent";

type WorkspaceUnreadStats = { unread: number; mentions: number };

type MembershipLike = { role: string };

interface SidebarHomeContentProps {
  isGuest: boolean | undefined;

  // Workspaces section
  homeWsOpen: boolean;
  setHomeWsOpen: (updater: (v: boolean) => boolean) => void;
  homeWsFilter: HomeFilter;
  setHomeWsFilter: (f: HomeFilter) => void;
  setShowCreateWorkspaceHome: (open: boolean) => void;
  homeDisplayWorkspaces: WorkspaceLike[];
  workspaceUnreads: Map<string, WorkspaceUnreadStats>;
  homeMembershipMap: Map<string, MembershipLike>;
  onScopeChange: (scope: string) => void;

  // DMs section
  homeDmsOpen: boolean;
  setHomeDmsOpen: (updater: (v: boolean) => boolean) => void;
  homeDmsFilter: HomeFilter;
  setHomeDmsFilter: (f: HomeFilter) => void;
  setShowNewDM: (open: boolean) => void;
  user: { id: string } | null | undefined;
  selectedChannel: string | null;
  onSelectChannel: (id: string | null, name?: string) => void;
  openChannelWithContextProp:
    | ((
        channelId: string,
        opts?: { workspaceId?: string | null; targetMessageId?: string },
      ) => void)
    | undefined;
  isUnread: (chId: string) => boolean;
  // Caller passes the workspace list verbatim — same loose `any[]` shape
  // SidebarProps.workspaceList uses.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workspaceList: any[];
}

export function SidebarHomeContent({
  isGuest,
  homeWsOpen,
  setHomeWsOpen,
  homeWsFilter,
  setHomeWsFilter,
  setShowCreateWorkspaceHome,
  homeDisplayWorkspaces,
  workspaceUnreads,
  homeMembershipMap,
  onScopeChange,
  homeDmsOpen,
  setHomeDmsOpen,
  homeDmsFilter,
  setHomeDmsFilter,
  setShowNewDM,
  user,
  selectedChannel,
  onSelectChannel,
  openChannelWithContextProp,
  isUnread,
  workspaceList,
}: SidebarHomeContentProps) {
  return (
    <>
      {/* My Workspaces */}
      {isGuest ? (
        <div className="px-2 pt-3">
          <div className="flex items-center px-2 mb-1">
            <SectionLabel variant="sidebar">Workspaces</SectionLabel>
          </div>
          <p className="text-muted-foreground/50 text-xs px-2 py-1">
            Sign up to create a workspace
          </p>
        </div>
      ) : (
        <div className="px-2 pt-3 group/section">
          <div className="flex items-center px-2 mb-1">
            <SidebarSectionToggle
              label="Workspaces"
              open={homeWsOpen}
              onToggle={() => setHomeWsOpen((v) => !v)}
            />
            <HomeSectionPill
              filter={homeWsFilter}
              onFilterChange={setHomeWsFilter}
              onCreateClick={() => setShowCreateWorkspaceHome(true)}
            />
          </div>
          {homeWsOpen && (
            <div className="space-y-0.5">
              {homeDisplayWorkspaces.map((ws) => {
                const stats = workspaceUnreads.get(ws.id);
                const membership = homeMembershipMap.get(ws.id);
                return (
                  <NavRow
                    key={ws.id}
                    onClick={() => onScopeChange(ws.id)}
                    className="group/ws"
                  >
                    <WorkspaceIcon
                      name={ws.name}
                      logoUrl={ws.logo_url}
                      slug={ws.slug}
                      size="md"
                    />
                    <span className="min-w-0 flex-1 truncate text-left">
                      {ws.name}
                    </span>
                    {membership && (
                      <span
                        className={cn(
                          "shrink-0 text-[9px] font-medium text-muted-foreground/60 bg-muted/50 px-1.5 py-0.5 rounded-full",
                          membership.role !== "collaborator" &&
                            "opacity-0 group-hover/ws:opacity-100 transition-opacity duration-150",
                        )}
                      >
                        {getRoleLabel(membership.role)}
                      </span>
                    )}
                    {(stats?.mentions ?? 0) > 0 && (
                      <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                        {stats!.mentions > 99 ? "99+" : stats!.mentions}
                      </span>
                    )}
                    {(stats?.unread ?? 0) > 0 &&
                      (stats?.mentions ?? 0) === 0 && (
                        <span className="h-2 w-2 rounded-full bg-foreground/50 shrink-0" />
                      )}
                  </NavRow>
                );
              })}
              {homeDisplayWorkspaces.length === 0 &&
                (homeMembershipMap.size === 0 ? (
                  // The user has ZERO workspaces total — gate the CTA
                  // on membership count, NOT on the active filter.
                  // Initial-render default is `homeWsFilter='recent'`,
                  // and a brand-new user lands there with no workspaces;
                  // scoping the CTA to `homeWsFilter === 'all'` (the
                  // first cut of this empty state) hid the button
                  // entirely on the default filter. Mirrors
                  // `DmListEmptyState` (DMList.tsx:410) styling so the
                  // two sibling sections feel symmetric.
                  <EmptyState
                    layout="inset"
                    title="No workspaces yet. Create one to invite your team."
                    action={
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setShowCreateWorkspaceHome(true)}
                      >
                        Create a workspace
                      </Button>
                    }
                  />
                ) : (
                  // User has workspaces but none match the current
                  // filter (e.g. Recent slice is empty). A CTA here
                  // would be misleading — they already have workspaces,
                  // they just need to switch filters.
                  <p className="text-muted-foreground/50 text-xs px-2 py-1">
                    No workspaces
                  </p>
                ))}
            </div>
          )}
        </div>
      )}

      <Separator className="mt-3" />

      {/* Direct Messages (global) */}
      <div className="px-2 pt-3 flex-1 group/section">
        <div className="flex items-center px-2 mb-1">
          <SidebarSectionToggle
            label="Direct Messages"
            open={homeDmsOpen}
            onToggle={() => setHomeDmsOpen((v) => !v)}
          />
          <HomeSectionPill
            filter={homeDmsFilter}
            onFilterChange={setHomeDmsFilter}
            onCreateClick={isGuest ? undefined : () => setShowNewDM(true)}
          />
        </div>
        {homeDmsOpen && user && (
          <DMList
            workspaceId={null}
            currentUserId={user.id}
            selectedChannel={selectedChannel}
            onSelectChannel={(id) =>
              id === null
                ? onSelectChannel(null)
                : openChannelWithContextProp
                  ? openChannelWithContextProp(id, {
                      workspaceId: null,
                    })
                  : onSelectChannel(id)
            }
            isUnread={isUnread}
            recentOnly={isGuest ? false : homeDmsFilter === "recent"}
            onShowAll={() => setHomeDmsFilter("all")}
            myWorkspaces={workspaceList}
          />
        )}
      </div>
    </>
  );
}
